// 3-Plan-Subscribe-Cards (Zustand A) im 21st-Style. Click ruft Stripe-Checkout.

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
import type { CoachingPlan } from '@/lib/coaching-api';

interface SubscribeCardsProps {
  onSubscribe: (plan: CoachingPlan) => Promise<void>;
}

const PLANS: ReadonlyArray<{
  key: CoachingPlan;
  label: string;
  amount: string;
  interval: string;
  saving?: string;
  highlight: boolean;
  features: ReadonlyArray<string>;
}> = [
  {
    key: 'quarterly',
    label: 'Quarterly',
    amount: '649 €',
    interval: '/ 3 Monate',
    highlight: false,
    features: ['3x pro Woche à 1h', 'Mindest-Laufzeit 3 Monate', 'Cancel jederzeit'],
  },
  {
    key: 'half_yearly',
    label: 'Half-yearly',
    amount: '1.227 €',
    interval: '/ 6 Monate',
    saving: '-5,5 %',
    highlight: true,
    features: ['3x pro Woche à 1h', '6 Monate volle Begleitung', '5,5 % Ersparnis'],
  },
  {
    key: 'yearly',
    label: 'Yearly',
    amount: '2.427 €',
    interval: '/ Jahr',
    saving: '-6,5 %',
    highlight: false,
    features: ['3x pro Woche à 1h', 'Volles Trading-Jahr', '6,5 % Ersparnis'],
  },
];

export function SubscribeCards({ onSubscribe }: SubscribeCardsProps) {
  const [loadingPlan, setLoadingPlan] = useState<CoachingPlan | null>(null);

  async function handleClick(plan: CoachingPlan) {
    setLoadingPlan(plan);
    try {
      await onSubscribe(plan);
    } finally {
      setLoadingPlan(null);
    }
  }

  return (
    <div className="grid gap-3 md:grid-cols-3">
      {PLANS.map((plan, i) => (
        <motion.div
          key={plan.key}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.06, type: 'spring', stiffness: 240, damping: 24 }}
          className={`glass-strong relative rounded-2xl border p-5 ${
            plan.highlight ? 'border-amber-500/50' : 'border-white/10'
          }`}
        >
          {plan.highlight ? (
            <span className="absolute -top-2 left-5 rounded-full border border-amber-500/40 bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-200">
              Empfehlung
            </span>
          ) : null}
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-[14px] font-bold text-neutral-100">{plan.label}</h3>
            {plan.saving ? (
              <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300">
                {plan.saving}
              </span>
            ) : null}
          </div>
          <div className="mb-1 flex items-baseline gap-1">
            <span className="text-3xl font-bold tabular text-neutral-100">{plan.amount}</span>
            <span className="text-[12px] text-neutral-500">{plan.interval}</span>
          </div>
          <ul className="my-4 space-y-1.5 text-[12px] text-neutral-300">
            {plan.features.map((f, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <Check className="mt-0.5 size-3.5 shrink-0 text-amber-400" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => void handleClick(plan.key)}
            disabled={loadingPlan !== null}
            className={`inline-flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-2 text-[12px] font-semibold transition-colors disabled:opacity-40 ${
              plan.highlight
                ? 'border border-amber-500/50 bg-amber-500/20 text-amber-200 hover:bg-amber-500/30'
                : 'border border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20'
            }`}
          >
            {loadingPlan === plan.key ? (
              <Loader2 className="size-3 animate-spin" />
            ) : null}
            Abonnieren
          </button>
        </motion.div>
      ))}
    </div>
  );
}
